import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

interface AppStore {
  /** Epoch time after which to show the match auto banner */
  autoBannerDismissedUntil: number;
  /** Set the epoch time after which to show the matcha auto banner */
  setAutoBannerDismissedUntil: (date: number) => void;
  /** The current block number */
  blockNumber: number | undefined;
  /** Set the current block number */
  setBlockNumber: (blockNumber: number) => void;
  /** Whether the user has dismissed the limit order fill info */
  dismissedLimitOrderFillInfo: boolean;
  /** Set whether the user has dismissed the limit order fill info */
  setDismissedLimitOrderFillInfo: (dismissed: boolean) => void;
  /** List of enabled experiments */
  experiments: string[];
  /** Set the list of enabled experiments */
  setExperiments: (experiments: string[]) => void;
  /** Whether the chart is expanded in token profile */
  isChartExpanded: boolean;
  /** Set whether the chart is expanded */
  setIsChartExpanded: (expanded: boolean) => void;
}

export const useAppStore = createWithEqualityFn<AppStore>()(
  persist(
    (set) => ({
      autoBannerDismissedUntil: new Date().getTime(),
      blockNumber: undefined,
      dismissedLimitOrderFillInfo: false,
      experiments: [],
      isChartExpanded: false,
      setAutoBannerDismissedUntil: (autoBannerDismissedUntil: number) =>
        set({ autoBannerDismissedUntil }),
      setBlockNumber: (blockNumber: number) => set({ blockNumber }),
      setDismissedLimitOrderFillInfo: (dismissedLimitOrderFillInfo: boolean) =>
        set({ dismissedLimitOrderFillInfo }),
      setExperiments: (experiments) => set({ experiments }),
      setIsChartExpanded: (isChartExpanded: boolean) => set({ isChartExpanded }),
    }),
    {
      name: 'app-store',
      partialize: (state) => ({
        autoBannerDismissedUntil: state.autoBannerDismissedUntil,
        dismissedLimitOrderFillInfo: state.dismissedLimitOrderFillInfo,
        isChartExpanded: state.isChartExpanded,
      }),
    },
  ),
  shallow,
);

export const blockNumberSelectors = (state: AppStore) => ({
  blockNumber: state.blockNumber,
  setBlockNumber: state.setBlockNumber,
});

export const dismissedLimitOrderFillInfoSelectors = (state: AppStore) => ({
  dismissedLimitOrderFillInfo: state.dismissedLimitOrderFillInfo,
  setDismissedLimitOrderFillInfo: state.setDismissedLimitOrderFillInfo,
});
export const autoBannerSelectors = ({
  autoBannerDismissedUntil,
  setAutoBannerDismissedUntil,
}: AppStore) => ({
  autoBannerDismissedUntil,
  setAutoBannerDismissedUntil,
});
export const experimentsSelectors = ({ experiments, setExperiments }: AppStore) => ({
  experiments,
  setExperiments,
});

export const chartExpandedSelectors = (state: AppStore) => ({
  isChartExpanded: state.isChartExpanded,
  setIsChartExpanded: state.setIsChartExpanded,
});
